import { ReactNode, useCallback, useContext, useMemo, useState } from 'react'
import { CampaignContext, ProjectContext, TemplateContext } from '../../contexts'
import { useTranslation } from 'react-i18next'
import { DataTable, InfoTable } from '../../ui'
import Button from '../../ui/Button'
import Modal from '../../ui/Modal'
import Iframe from '../../ui/Iframe'
import Heading from '../../ui/Heading'
import { snakeToTitle } from '../../utils'
import { Audit } from '../../types'
import api from '../../api'
import { useResolver } from '../../hooks'
import { Tooltip } from '../../ui/Tooltip'
import Chip from '../../ui/Chip'
import Tag, { TagGroup } from '../../ui/Tag'
import VariantSelector from './variants/VariantSelector'
import LocaleSelector from './locale/LocaleSelector'
import { localeOption } from './template/TemplateContextProvider'

export default function CampaignHistory() {
    const [project] = useContext(ProjectContext)
    const { t } = useTranslation()
    const [campaign] = useContext(CampaignContext)
    const { currentTemplate } = useContext(TemplateContext)
    const [htmlPreview, setHtmlPreview] = useState<string>()

    const resolveHtml = (resolved: any): ReactNode => {
        if (typeof resolved === 'string') {
            return <Button variant="primary" size="tiny" onClick={(e) => { e.preventDefault(); setHtmlPreview(resolved) }}>{t('preview')}</Button>
        }
        return resolved
    }

    const listOfChanges = (item: Record<string, any>, prefix?: string): { old: Record<string, ReactNode>, new: Record<string, ReactNode> } => {
        const exclusions = ['delivery', 'version', 'project_id', 'updated_at', 'created_at', 'id', 'json', 'provider', 'subscription', 'lists', 'exclusion_lists', 'mjml']
        const oldObj: Record<string, ReactNode> = {}
        const newObj: Record<string, ReactNode> = {}
        for (const key of Object.keys(item)) {
            if (exclusions.includes(key)) continue
            const value = item[key]
            const keyName = prefix ? `${prefix}_${key}` : key
            if (value != null && typeof value === 'object' && !('old' in value) && !('new' in value)) {
                const nested = listOfChanges(value, keyName)
                Object.assign(oldObj, nested.old)
                Object.assign(newObj, nested.new)
                continue
            }
            const oldVal = value?.old
            const newVal = value?.new
            if (oldVal === undefined && newVal === undefined) continue
            if ((oldVal != null && typeof oldVal === 'object' && !Array.isArray(oldVal))
                || (newVal != null && typeof newVal === 'object' && !Array.isArray(newVal))) {
                const allKeys = new Set([
                    ...Object.keys(oldVal ?? {}),
                    ...Object.keys(newVal ?? {}),
                ])
                for (const k of allKeys) {
                    if (exclusions.includes(k)) continue
                    const ov = oldVal?.[k]
                    const nv = newVal?.[k]
                    if (k === 'html') {
                        oldObj[`${keyName}_${k}`] = resolveHtml(ov)
                        newObj[`${keyName}_${k}`] = resolveHtml(nv)
                        continue
                    }
                    oldObj[`${keyName}_${k}`] = (ov != null && typeof ov === 'object') ? JSON.stringify(ov) : ov
                    newObj[`${keyName}_${k}`] = (nv != null && typeof nv === 'object') ? JSON.stringify(nv) : nv
                }
                continue
            }
            if (key === 'html') {
                oldObj[keyName] = resolveHtml(oldVal)
                newObj[keyName] = resolveHtml(newVal)
                continue
            }
            const resolve = (v: any) => {
                if (Array.isArray(v)) return undefined
                if (v != null && typeof v === 'object') return JSON.stringify(v)
                return v
            }
            const oldDisplay = resolve(oldVal)
            const newDisplay = resolve(newVal)
            if (oldDisplay === undefined && newDisplay === undefined) continue
            oldObj[keyName] = oldDisplay
            newObj[keyName] = newDisplay
        }
        return { old: oldObj, new: newObj }
    }

    const [history] = useResolver(
        useCallback(
            async () => await api.campaigns.history(project.id, campaign.id)
                .then(
                    items => items.map((item) => ({
                        ...item,
                        changes: listOfChanges(item.object_changes),
                    })),
                ), [campaign.id],
        ),
    )

    const [archivedTemplates] = useResolver(
        useCallback(
            async () => await api.campaigns.templates(project.id, campaign.id, true)
                .then(templates => new Map(
                    templates.filter(template => template.deleted_at)
                        .map(template => [template.id, template] as const),
                )), [project.id, campaign.id],
        ),
    )

    const archivedTemplate = (item: Audit) => item.item_type === 'templates'
        ? archivedTemplates?.get(item.item_id)
        : undefined

    const filteredHistory = useMemo(() => {
        if (!history) return []
        if (!currentTemplate) return history
        return history.filter(item =>
            item.item_type !== 'templates'
            || item.item_id === currentTemplate.id
            || archivedTemplates?.has(item.item_id),
        )
    }, [history, currentTemplate, archivedTemplates])

    return (<>
        <Heading title={t('history')} size="h3" actions={
            <>
                <VariantSelector />
                <LocaleSelector />
            </>
        } />
        <DataTable
            columns={[
                {
                    key: 'event',
                    title: t('event'),
                    cell: ({ item }) => {
                        const archived = archivedTemplate(item)
                        return <>
                            <Chip title={snakeToTitle(item.event)} subtitle={snakeToTitle(item.item_type)} />
                            {archived && (
                                <TagGroup>
                                    <Tag variant="info" size="tiny">
                                        {[localeOption(archived.locale).label, archived.name].filter(Boolean).join(' · ')}
                                    </Tag>
                                    <Tag variant="plain" size="tiny">{t('archived')}</Tag>
                                </TagGroup>
                            )}
                        </>
                    },
                    wrap: false,
                },
                {
                    key: 'admin',
                    title: t('admin'),
                    cell: ({ item }) => {
                        const name = [item.admin_first_name, item.admin_last_name].filter(Boolean).join(' ')
                        if (!name) return item.admin_email
                        return <Tooltip content={<Chip title={name} subtitle={item.admin_email ?? ''} />}>
                            <span>{name}</span>
                        </Tooltip>
                    },
                    wrap: false,
                },
                {
                    key: 'old',
                    title: t('audit_old'),
                    cell: ({ item }) => Object.keys(item.changes.old).length > 0
                        ? <InfoTable rows={item.changes.old} size="small" />
                        : null,
                    minWidth: '200px',
                },
                {
                    key: 'new',
                    title: t('audit_new'),
                    cell: ({ item }) => Object.keys(item.changes.new).length > 0
                        ? <InfoTable rows={item.changes.new} size="small" />
                        : null,
                    minWidth: '200px',
                },
                {
                    key: 'created_at',
                    wrap: false,
                },
            ]}
            items={filteredHistory}
        />
        <Modal
            title={t('preview')}
            size="fullscreen"
            open={htmlPreview != null}
            onClose={() => setHtmlPreview(undefined)}
        >
            {htmlPreview && <Iframe content={htmlPreview} fullHeight={true} width="100%" />}
        </Modal>
    </>
    )
}
